<template>
  <div v-if="trialState.visible" class="trial-mask" @click.self="handleClose">
    <div class="trial-dialog">
      <template v-if="trialState.view === 'select'">
        <h2 class="trial-title">免费试用</h2>
        <p class="trial-desc">免费体验全部核心功能</p>

        <div class="feature-list">
          <BaseCheckbox
            v-for="feature in featureOptions"
            :key="feature.value"
            :value="isFeatureSelected(feature.value)"
            variant="feature"
            icon-position="right"
            @input="handleSetFeature(feature.value, $event)"
          >
            <span>{{ feature.label }}</span>
          </BaseCheckbox>
        </div>

        <div class="trial-tip">
          <img class="trial-tip-icon" :src="tipIcon" alt="">
          <span>免费试用 7 天，最多可选择 2 个赛道</span>
        </div>

        <div v-if="trialState.errorMessage" class="trial-error">{{ trialState.errorMessage }}</div>

        <button class="trial-primary" type="button" @click="handleApplyTrial">立即开通试用</button>
        <button class="trial-link" type="button" @click="handleContactService">有问题？联系客服</button>
      </template>

      <template v-else-if="trialState.view === 'success'">
        <img class="result-icon" :src="successIcon" alt="开通成功">
        <h2 class="result-title">免费试用已开通</h2>
        <p class="result-desc">免费体验全部核心功能</p>
        <div class="result-info">
          <div>您已成功开通了 <span class="trial-duration-text">7 天免费试用</span></div>
          <div class="expire-row">
            <img class="expire-icon" :src="timeIcon" alt="">
            <span class="expire-label">有效期至</span>
            <span class="expire-time">{{ expireTimeText }}</span>
          </div>
        </div>
        <button class="trial-primary result-button" type="button" @click="handleStartExperience">立即开始体验</button>
        <button class="trial-link back-link" type="button" @click="handleClose">
          <img class="back-icon" :src="backIcon" alt="">
          返回继续浏览
        </button>
      </template>

      <template v-else>
        <img class="result-icon" :src="trialedIcon" alt="已领取过试用">
        <h2 class="result-title">当前账号已领取过试用</h2>
        <p class="result-desc">每个账号仅可领取一次免费试用机会</p>
        <button class="trial-primary result-button" type="button" @click="handleBuyPackage">立即购买套餐</button>
        <button class="trial-link back-link" type="button" @click="handleClose" style="margin-top: 120px">
          <img class="back-icon" :src="backIcon" alt="">
          返回继续浏览
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import trial from '@/store/trial'
import BaseCheckbox from '@/components/BaseCheckbox.vue'
import tipIcon from '@/assets/images/icons/tip.svg'
import successIcon from '@/assets/images/pay/success.svg'
import trialedIcon from '@/assets/images/Trialed.svg'
import timeIcon from '@/assets/images/icons/timeBlue.svg'
import backIcon from '@/assets/images/icons/back.svg'

export default {
  name: 'GlobalTrialDialog',
  components: {BaseCheckbox},
  data() {
    return {
      trialState: trial.state,
      tipIcon,
      successIcon,
      trialedIcon,
      timeIcon,
      backIcon
    }
  },
  computed: {
    // 方法：把试用有效期时间戳格式化为弹窗展示文案
    expireTimeText() {
      const expiresAt = this.trialState.currentRecord && this.trialState.currentRecord.trialExpiresAt

      // 逻辑分支：没有有效期数据时使用占位文案，避免弹窗出现空白
      if (!expiresAt) {
        return '--'
      }

      return this.formatDateTime(new Date(expiresAt))
    },
    // 关键状态：试用选项响应式读取目录，目录更新后弹窗同步更新。
    featureOptions() {
      return trial.getTrialFeatureOptions()
    }
  },
  methods: {
    // 方法：判断功能项是否已被当前账号选择
    isFeatureSelected(featureValue) {
      return this.trialState.selectedFeatures.indexOf(featureValue) > -1
    },
    // 方法：按复选组件传回的布尔值设置申请功能多选状态
    handleSetFeature(featureValue, checked) {
      trial.setTrialFeature(featureValue, checked)
    },
    // 关键节点：提交免费试用申请，成功后切换为开通结果弹窗
    async handleApplyTrial() {
      await trial.applyTrial()
    },
    // 关键节点：开通成功后进入原 Web 端试用地址
    handleStartExperience() {
      trial.closeTrialDialog()
      trial.openTrialTarget()
    },
    // 方法：已领取过试用时跳转套餐服务页
    handleBuyPackage() {
      trial.closeTrialDialog()
      this.$router.push('/package-service')
    },
    // 方法：跳转客服入口，协助用户处理试用问题
    handleContactService() {
      trial.closeTrialDialog()
      this.$router.push('/enterprise-wechat')
    },
    // 方法：关闭试用弹窗
    handleClose() {
      trial.closeTrialDialog()
    },
    // 方法：补齐一位数字，供日期格式化复用
    padNumber(value) {
      return String(value).padStart(2, '0')
    },
    // 方法：格式化日期时间为 YYYY-MM-DD HH:mm:ss
    formatDateTime(date) {
      const year = date.getFullYear()
      const month = this.padNumber(date.getMonth() + 1)
      const day = this.padNumber(date.getDate())
      const hours = this.padNumber(date.getHours())
      const minutes = this.padNumber(date.getMinutes())
      const seconds = this.padNumber(date.getSeconds())

      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    }
  }
}
</script>

<style scoped lang="less">
.trial-mask{
  position: fixed;
  z-index: 9998;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.42);
  display: flex;
  align-items: center;
  justify-content: center;
}

.trial-dialog{
  width: 600px;
  min-height: 462px;
  max-height: calc(100vh - 48px);
  border-radius: 8px;
  background: #fff;
  padding: 48px 60px 30px;
  box-sizing: border-box;
  overflow-y: auto;
  color: #000000D9;
  text-align: center;
}

.trial-title{
  margin: 0;
  color: #000;
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;
}

.trial-desc{
  margin: 10px 0 0;
  color: #00000073;
  font-size: 12px;
  line-height: 18px;
}

.feature-list{
  margin-top: 34px;
  max-height: 372px;
  padding-right: 4px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 12px;
  row-gap: 12px;
  overflow-y: auto;

  /deep/ .base-checkbox-feature{
    margin-bottom: 0;
  }
}

.trial-tip{
  width: 100%;
  height: 34px;
  margin-top: 14px;
  padding: 0 12px;
  border-radius: 4px;
  background: #EDF5FF;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  color: #2F7BFF;
  font-size: 11px;
  line-height: 16px;
  text-align: left;
}

.trial-tip-icon{
  width: 13px;
  height: 13px;
  margin-right: 7px;
  display: block;
  flex: 0 0 auto;
}

.trial-error{
  height: 18px;
  margin-top: 8px;
  color: #ff4d4f;
  font-size: 11px;
  line-height: 18px;
}

.trial-primary{
  width: 100%;
  height: 36px;
  margin-top: 20px;
  border: none;
  border-radius: 4px;
  background: linear-gradient(108deg, #7FDBFF 0%, #367CFF 58%, #8AAEFF 100%);
  color: #fff;
  font-size: 12px;
  line-height: 18px;
  cursor: pointer;
}

.trial-link{
  margin-top: 14px;
  border: none;
  background: transparent;
  color: #2F7BFF;
  font-size: 11px;
  line-height: 18px;
  cursor: pointer;
}

.result-icon{
  width: 66px;
  height: 66px;
  margin: 0 auto 18px;
  display: block;
}

.result-title{
  margin: 0;
  color: #000;
  font-size: 20px;
  line-height: 28px;
  font-weight: 700;
}

.result-desc{
  margin: 10px 0 0;
  color: #00000073;
  font-size: 12px;
  line-height: 18px;
}

.result-info{
  margin-top: 58px;
  color: #595959;
  font-size: 13px;
  line-height: 22px;
}

.trial-duration-text{
  color: #2F7BFF;
}

.expire-row{
  margin-top: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #000000A6;
}

.expire-label{
  color: #000000A6;
}

.expire-time{
  margin-left: 4px;
  color: #2F7BFF;
}

.expire-icon{
  width: 14px;
  height: 14px;
  margin-right: 6px;
  display: block;
}

.result-button{
  margin-top: 66px;
}

.back-link{
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.back-icon{
  width: 12px;
  height: 12px;
  margin-right: 4px;
  display: block;
}
</style>
